import React, { useCallback, useReducer, useEffect } from 'react'
import PropTypes from 'prop-types'

export const initialState = {
  ids: [],
  idSelectionStates: {},
  idOrder: {},
  startIndexClicked: null,
  lastIndexShiftClicked: null,
}

export function reducer(state, action) {
  const { idSelectionStates, idOrder, ids, startIndexClicked, lastIndexShiftClicked } = state
  switch (action.type) {
    case 'select-item': {
      const { id, shiftClick } = action
      const selected = idSelectionStates[id]
      const currentIndexClicked = idOrder[id]
      if (shiftClick && startIndexClicked != null && startIndexClicked !== currentIndexClicked) {
        const startIdClicked = ids[startIndexClicked]
        const weGunSelectOrDeselect = idSelectionStates[startIdClicked]
        let firstIndexClicked = startIndexClicked
        let lastIndexClicked = currentIndexClicked
        if (firstIndexClicked > lastIndexClicked) {
          const swapIndex = firstIndexClicked
          firstIndexClicked = lastIndexClicked
          lastIndexClicked = swapIndex
        }
        const newIdSelectionStates = { ...idSelectionStates }
        for (let i = firstIndexClicked; i <= lastIndexClicked; i++) {
          newIdSelectionStates[ids[i]] = weGunSelectOrDeselect
        }
        // Handle the outliers (outside of click-range)
        if (lastIndexShiftClicked != null) {
          // KEY: S - StartIndexClicked, L - LastIndexShiftClicked, C - CurrentIndexClick
          // scenario 1: ......S......L.....C -> do nothing, already handled above
          // scenario 2: ......S......CxxxxxL -> unselect the x's
          // scenario 3: ......C......SxxxxxL -> unselect the x's,
          // scenario 4: ......C......L.....S -> do nothing, already handled above
          // scenario 5: ......LxxxxxxC.....S -> unselect the x's
          if (lastIndexShiftClicked > lastIndexClicked) {
            for (let i = lastIndexClicked + 1; i <= lastIndexShiftClicked; i++) {
              newIdSelectionStates[ids[i]] = !weGunSelectOrDeselect
            }
          } else if (lastIndexShiftClicked < firstIndexClicked) {
            for (let i = lastIndexShiftClicked; i < firstIndexClicked; i++) {
              newIdSelectionStates[ids[i]] = !weGunSelectOrDeselect
            }
          }
        }
        return {
          ...state,
          idSelectionStates: newIdSelectionStates,
          lastIndexShiftClicked: currentIndexClicked,
        }
      }
      return {
        ...state,
        idSelectionStates: { ...idSelectionStates, [id]: !selected },
        startIndexClicked: currentIndexClicked,
        lastIndexShiftClicked: shiftClick ? currentIndexClicked : null,
      }
    }
    case 'change-items': {
      const { items } = action
      const newIds = []
      const newIdSelectionStates = {}
      const newIdOrder = {}
      items.forEach((item, index) => {
        const { id } = item
        newIds.push(id)
        newIdSelectionStates[id] = idSelectionStates[id] || false
        newIdOrder[id] = index
      })
      return {
        ids: newIds,
        idSelectionStates: newIdSelectionStates,
        idOrder: newIdOrder,
      }
    }
    case 'toggle-select-all': {
      const { selected } = action
      const newIdSelectionStates = {}
      Object.keys(idSelectionStates).forEach((id) => {
        newIdSelectionStates[id] = selected
      })
      return { ...state, idSelectionStates: newIdSelectionStates }
    }
    default:
      throw new Error(`Unknown Action type: ${action.type}`)
  }
}
export default function useSelection({ items }) {
  const [{ idSelectionStates }, dispatch] = useReducer(reducer, initialState)
  const selectItem = useCallback((id, shiftClick = false) => {
    dispatch({ type: 'select-item', id, shiftClick })
  }, [])
  const selectAll = useCallback(() => {
    dispatch({ type: 'toggle-select-all', selected: true })
  }, [])
  const deselectAll = useCallback(() => {
    dispatch({ type: 'toggle-select-all', selected: false })
  }, [])
  const changeItems = useCallback((newItems) => {
    dispatch({ type: 'change-items', items: newItems })
  }, [])
  useEffect(() => {
    dispatch({ type: 'change-items', items })
  }, [items])
  return {
    idSelectionStates,
    selectItem,
    selectAll,
    deselectAll,
    changeItems,
  }
}

export const UseSelectionProps = ({ ...props }) => <div {...props} />

UseSelectionProps.propTypes = {
  /** List of unique identifiers of selectable children, in displayed order. */
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    })
  ),
}
